import { icons } from "@/utils/icons"
import { Range } from "react-range"
import millionIcon from "@/images/icons/million.svg"
import Money from "@/Components/Money"
import { playSliderToggle } from "@/utils/audioManager"
import classNames from "classnames"
import React, { useCallback, useState } from "react"

interface TradeMoneySliderProps {
  value: number
  min: number
  setValue: (value: number) => void
  max: number
  minTestId?: TestId
  maxTestId?: TestId
  disabled?: boolean
  className?: string
}

const TradeMoneySlider: React.FC<TradeMoneySliderProps> = ({
  max,
  min,
  setValue,
  minTestId,
  maxTestId,
  value,
  disabled,
  className,
}) => {
  const [particles, setParticles] = useState<
    Array<{
      id: number
      leftPercent: number
      dx: number
      dy: number
      dr: number
    }>
  >([])

  const createBurst = useCallback(
    (percent: number) => {
      // Subtle, localized particle burst near the thumb
      const count = 5
      const now = Date.now()
      const center = Math.max(0, Math.min(100, percent))
      const newOnes = Array.from({ length: count }).map((_, i) => {
        const radius = 6 + Math.random() * 10 // small radius spread
        const theta = Math.random() * Math.PI * 2
        const dx = radius * Math.cos(theta)
        const dy = radius * Math.sin(theta)
        return {
          id: now + i,
          leftPercent: center,
          dx,
          dy,
          dr: (Math.random() - 0.5) * 60,
        }
      })
      setParticles((prev) => [...prev, ...newOnes])
      // Cleanup after animation
      setTimeout(() => {
        setParticles((prev) =>
          prev.filter((p) => newOnes.findIndex((n) => n.id === p.id) === -1)
        )
      }, 520)
    },
    [setParticles]
  )

  const handleChange = useCallback(
    (values: number[]) => {
      // react-range clamps to `max` before snapping to `step`, then rounds half
      // up — so a `max` that isn't a multiple of `step` (any money amount ending
      // in 6-9) can snap above it. Re-clamp, or the guards below unmount us.
      const newVal = Math.min(Math.max(values[0], min), max)
      if (newVal === value) return
      setValue(newVal)
      if (!disabled) {
        playSliderToggle()
        const percent = ((newVal - min) / (max - min)) * 100
        createBurst(percent)
      }
    },
    [createBurst, disabled, max, min, setValue, value]
  )

  const handleIncrement = useCallback(() => {
    const newVal = Math.min(value + 10, max)
    if (newVal === value) return
    setValue(newVal)
    if (!disabled) {
      playSliderToggle()
      const percent = ((newVal - min) / (max - min)) * 100
      createBurst(percent)
    }
  }, [value, max, min, setValue, disabled, createBurst])

  const handleDecrement = useCallback(() => {
    const newVal = Math.max(value - 10, min)
    if (newVal === value) return
    setValue(newVal)
    if (!disabled) {
      playSliderToggle()
      const percent = ((newVal - min) / (max - min)) * 100
      createBurst(percent)
    }
  }, [value, min, max, setValue, disabled, createBurst])

  // Guards must stay below every hook — bailing out above them changes the hook
  // count between renders and throws away this component's state.
  if (min > value) return null
  if (value > max) return null
  if (min >= max) return null

  return (
    <div
      className={classNames(
        "trade-slider pt-16 md:pt-12 px-0 relative",
        className
      )}
    >
      {/* Slider with buttons on sides */}
      <div className="flex items-center gap-3 md:gap-4">
        {/* Decrement Button (Left) */}
        <button
          onClick={handleDecrement}
          disabled={disabled || value <= min}
          className={classNames(
            "flex-shrink-0 rounded-full flex items-center justify-center font-bold z-10 relative overflow-hidden",
            "w-12 h-12 md:w-10 md:h-10 text-2xl md:text-lg",
            "transition-all duration-200 ease-out",
            "hover:scale-110 hover:shadow-xl hover:shadow-yellow-500/50 hover:brightness-110",
            "active:scale-90 active:brightness-90 active:shadow-inner",
            "focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 focus:ring-offset-transparent",
            "before:absolute before:inset-0 before:rounded-full before:bg-white/0 before:transition-all before:duration-300",
            "active:before:bg-white/30 active:before:scale-150",
            {
              "bg-gradient-to-br from-yellow-400 to-yellow-600 border-2 border-yellow-300 text-white shadow-lg":
                !disabled && value > min,
              "bg-gray-700 border-2 border-gray-600 text-gray-400 cursor-not-allowed":
                disabled || value <= min,
            }
          )}
        >
          <span className="relative z-10">−</span>
        </button>

        {/* Slider Track */}
        <div className="flex-1 relative">
          <Range
            disabled={disabled}
            label={value.toString()}
            step={10}
            min={min}
            max={max}
            values={[value]}
            onChange={handleChange}
            renderTrack={({ props, children }) => {
              // Calculate percent for selected range
              const percent = ((value - min) / (max - min)) * 100
              return (
                <div
                  className={classNames(
                    "relative h-3 md:h-2 rounded-full transition-all duration-150 w-full",
                    {
                      "bg-gray-600": disabled,
                      "bg-gradient-slider-track-dark": !disabled,
                    }
                  )}
                  {...props}
                >
                  {/* Selected range overlay */}
                  {!disabled && (
                    <div
                      className="absolute left-0 top-0 h-full rounded-full bg-gradient-slider-track pointer-events-none"
                      style={{ width: `${percent}%` }}
                    />
                  )}
                  {/* Particle overlay */}
                  {!disabled && (
                    <div className="pointer-events-none absolute inset-0 overflow-visible">
                      {particles.map((p) => (
                        <span
                          key={p.id}
                          className="tm-gold-particle"
                          style={
                            {
                              left: `${p.leftPercent}%`,
                              top: "-4px",
                              ["--dx" as any]: `${p.dx}px`,
                              ["--dy" as any]: `${p.dy}px`,
                              ["--dr" as any]: `${p.dr}deg`,
                            } as React.CSSProperties as any
                          }
                        />
                      ))}
                    </div>
                  )}
                  {children}
                </div>
              )
            }}
            renderThumb={({ props }) => (
              <div
                className="relative flex flex-col"
                {...props}
                style={{ ...props.style }}
              >
                <Money
                  money={value}
                  className={classNames(
                    "gap-1 text-3xl md:text-xl w-[250%] items-center absolute -top-12 md:-top-8 uppercase font-poppins font-bold",
                    {
                      "text-range-marker": !disabled,
                      "text-white/80": disabled,
                    }
                  )}
                  iconClass="w-auto h-3/5 -translate-y-[1px]"
                />
                <div
                  className={classNames(
                    "rounded-full w-20 h-10 md:w-14 md:h-6 transition-all duration-150",
                    {
                      // Gold gradient thumb for enabled, gray for disabled
                      "bg-gradient-to-br from-yellow-400 to-yellow-600 border-yellow-300  shadow-sliderThumb":
                        !disabled,
                      "border-gray-500 bg-gray-700": disabled,
                    }
                  )}
                />
              </div>
            )}
          />
        </div>

        {/* Increment Button (Right) */}
        <button
          onClick={handleIncrement}
          disabled={disabled || value >= max}
          className={classNames(
            "flex-shrink-0 rounded-full flex items-center justify-center font-bold z-10 relative overflow-hidden",
            "w-12 h-12 md:w-10 md:h-10 text-2xl md:text-lg",
            "transition-all duration-200 ease-out",
            "hover:scale-110 hover:shadow-xl hover:shadow-yellow-500/50 hover:brightness-110",
            "active:scale-90 active:brightness-90 active:shadow-inner",
            "focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 focus:ring-offset-transparent",
            "before:absolute before:inset-0 before:rounded-full before:bg-white/0 before:transition-all before:duration-300",
            "active:before:bg-white/30 active:before:scale-150",
            {
              "bg-gradient-to-br from-yellow-400 to-yellow-600 border-2 border-yellow-300 text-white shadow-lg":
                !disabled && value < max,
              "bg-gray-700 border-2 border-gray-600 text-gray-400 cursor-not-allowed":
                disabled || value >= max,
            }
          )}
        >
          <span className="relative z-10">+</span>
        </button>
      </div>
      <div className="flex justify-between w-full items-center font-poppins pt-6 md:pt-4 px-2">
        <Money
          testId={minTestId}
          money={min}
          className={classNames("gap-1 text-xl md:text-base font-semibold", {
            "text-range-track": !disabled,
            "text-white/70": disabled,
          })}
          iconClass="h-3/5 -translate-y-[1px]"
        />
        <Money
          testId={maxTestId}
          money={max}
          className={classNames("gap-1 text-xl md:text-base font-semibold", {
            "text-range-track": !disabled,
            "text-white/70": disabled,
          })}
          iconClass="h-3/5 -translate-y-[1px]"
        />
      </div>
    </div>
  )
}

export default TradeMoneySlider
